import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookmark,
  faUpload,
  faTrash,
  faCopy,
  faCheck,
  faExpand,
  faDownload,
  faImage,
  faVideo,
  faSpinner,
  faFilter,
  faWandMagicSparkles,
  faFolderOpen,
  faRocket,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest, deleteRequest, postRequest } from "services/api/apiService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ENGINE_LABEL = {
  imagen4:     "Imagen 4",
  huggingface: "HuggingFace",
  replicate:   "FLUX Schnell",
  minimax:     "MiniMax",
  upload:      "Uploaded",
};

const ENGINE_COLOR = {
  imagen4:     "bg-blue-100 text-blue-700",
  huggingface: "bg-orange-100 text-orange-700",
  replicate:   "bg-green-100 text-green-700",
  minimax:     "bg-purple-100 text-purple-700",
  upload:      "bg-gray-100 text-gray-600",
};

const fmtDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

const AdLibraryPage = () => {
  const [assets, setAssets]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [copied, setCopied]         = useState(null);
  const [fullscreen, setFullscreen] = useState(null);
  const [dragOver, setDragOver]     = useState(false);
  const fileInputRef = useRef(null);
  const navigate     = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q   = typeFilter !== "all" ? `?type=${typeFilter}` : "";
      const res = await getRequest(`/api/ai-ad/library${q}`);
      setAssets(res.assets || []);
    } catch {
      toast.error("Unable to load the library.");
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let added = 0;
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append("file", file);
      form.append("name", file.name.replace(/\.[^.]+$/, ""));
      try {
        // Use the shared axios instance: it injects the correct auth token
        // (authToken) and the REACT_APP_API_URL base, and sets the multipart
        // boundary automatically for FormData bodies.
        const { asset } = await postRequest("/api/ai-ad/library/upload", form);
        setAssets((prev) => [asset, ...prev]);
        added++;
      } catch (err) {
        toast.error(`Error: ${file.name}`);
      }
    }
    if (added) toast.success(`${added} file(s) added to the library`);
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try {
      await deleteRequest(`/api/ai-ad/library/${id}`);
      setAssets((prev) => prev.filter((a) => a.id !== id));
      toast.success("Asset deleted");
    } catch {
      toast.error("Something went wrong while deleting.");
    }
  };

  const handleCopy = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const images = assets.filter((a) => a.type === "image").length;
  const videos = assets.filter((a) => a.type === "video").length;

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-5 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#221C51] to-[#4A3D9A] flex items-center justify-center shadow">
          <FontAwesomeIcon icon={faBookmark} className="text-white text-sm" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-[#221C51] leading-tight">Ad Library</h1>
          <p className="text-xs text-gray-400">{images} image{images !== 1 ? "s" : ""} · {videos} video{videos !== 1 ? "s" : ""}</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => navigate("/dashboard/ai-launcher")}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#221C51]/20 text-[#221C51] text-xs font-semibold hover:bg-[#221C51]/5 transition-all"
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[10px]" />
            AI Launcher
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#221C51] text-white text-xs font-semibold hover:bg-[#2d2566] transition-all disabled:opacity-50"
          >
            {uploading
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Uploading…</>
              : <><FontAwesomeIcon icon={faUpload} /> Upload</>
            }
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center gap-2 mb-4 shrink-0">
        <FontAwesomeIcon icon={faFilter} className="text-gray-400 text-xs" />
        {[
          { key: "all",   label: "All" },
          { key: "image", label: "Images", icon: faImage },
          { key: "video", label: "Videos", icon: faVideo },
        ].map(({ key, label, icon }) => (
          <button key={key} onClick={() => setTypeFilter(key)}
            className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-all ${
              typeFilter === key ? "bg-[#221C51] text-white border-[#221C51]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
            }`}>
            {icon && <FontAwesomeIcon icon={icon} className="text-[10px]" />}
            {label}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-400">{assets.length} asset{assets.length !== 1 ? "s" : ""}</span>
      </div>

      {/* ── Drop zone / Grid ── */}
      <div
        className={`flex-1 overflow-y-auto rounded-2xl transition-all ${dragOver ? "ring-2 ring-[#221C51] ring-dashed bg-[#221C51]/5" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {loading && (
          <div className="flex items-center justify-center h-48 text-gray-400 text-sm gap-2">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading…
          </div>
        )}

        {!loading && assets.length === 0 && (
          <div
            className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-gray-200 rounded-2xl gap-3 cursor-pointer hover:border-[#221C51]/40 hover:bg-[#221C51]/3 transition-all"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
              <FontAwesomeIcon icon={faFolderOpen} className="text-gray-300 text-3xl" />
            </div>
            <p className="text-sm font-medium text-gray-400">Your library is empty</p>
            <p className="text-xs text-gray-300 text-center max-w-xs">
              Drag and drop images or videos here, or click to upload.<br />
              Assets generated by the AI Launcher appear here automatically.
            </p>
            <span className="text-xs font-semibold text-[#221C51] bg-[#221C51]/10 px-3 py-1.5 rounded-full">
              + Upload files
            </span>
          </div>
        )}

        {!loading && assets.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 pb-4">
            {assets.map((a) => (
              <div key={a.id}
                className="group relative bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">

                {/* Thumbnail */}
                <div className="relative aspect-square bg-gray-100 cursor-pointer" onClick={() => setFullscreen(a)}>
                  {a.type === "image" ? (
                    <img src={a.url} alt={a.name || a.prompt || "asset"} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <video src={a.url} className="w-full h-full object-cover" muted />
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <FontAwesomeIcon icon={faExpand} className="text-white text-xl drop-shadow" />
                  </div>
                  {/* Type badge */}
                  <span className={`absolute top-2 left-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                    a.type === "video" ? "bg-purple-600 text-white" : "bg-blue-600 text-white"
                  }`}>
                    {a.type === "video" ? "VIDEO" : "IMG"}
                  </span>
                  {/* Engine badge */}
                  {a.engine && (
                    <span className={`absolute top-2 right-2 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${ENGINE_COLOR[a.engine] || "bg-gray-100 text-gray-500"}`}>
                      {ENGINE_LABEL[a.engine] || a.engine}
                    </span>
                  )}
                </div>

                {/* Info */}
                <div className="p-2.5">
                  {a.name ? (
                    <p className="text-xs font-semibold text-[#221C51] truncate mb-0.5">{a.name}</p>
                  ) : null}
                  {a.prompt && (
                    <p className="text-[11px] text-gray-500 line-clamp-2 leading-snug mb-1">
                      {a.prompt}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-400 mb-2">{fmtDate(a.created_at || a.createdAt)}</p>

                  {/* Create Ad button */}
                  <button
                    onClick={() => navigate("/dashboard/create-ad-from-library", { state: { asset: a } })}
                    className="w-full flex items-center justify-center gap-1 text-[10px] py-1.5 rounded-lg bg-[#221C51] text-white hover:bg-[#2d2566] font-semibold transition-all mb-1.5"
                  >
                    <FontAwesomeIcon icon={faRocket} className="text-[9px]" />
                    Create Ad
                  </button>

                  {/* Actions */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleCopy(a.url, a.id)}
                      title="Copy URL"
                      className="flex-1 flex items-center justify-center gap-1 text-[10px] py-1.5 rounded-lg bg-[#221C51]/5 text-[#221C51] hover:bg-[#221C51]/10 font-semibold transition-all"
                    >
                      <FontAwesomeIcon icon={copied === a.id ? faCheck : faCopy} className="text-[10px]" />
                      {copied === a.id ? "Copied!" : "Copy URL"}
                    </button>
                    <a
                      href={a.url} download target="_blank" rel="noreferrer"
                      title="Download"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 transition-all"
                    >
                      <FontAwesomeIcon icon={faDownload} className="text-[10px]" />
                    </a>
                    <button
                      onClick={() => handleDelete(a.id)}
                      title="Delete"
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-all"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Upload drop card */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-[#221C51]/50 hover:bg-[#221C51]/3 transition-all text-gray-300 hover:text-[#221C51]"
            >
              <FontAwesomeIcon icon={faUpload} className="text-2xl" />
              <span className="text-[10px] font-semibold">Add</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Fullscreen lightbox ── */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setFullscreen(null)}
        >
          {fullscreen.type === "video"
            ? <video src={fullscreen.url} controls autoPlay loop className="max-w-full max-h-full rounded-xl" onClick={(e) => e.stopPropagation()} />
            : <img src={fullscreen.url} alt="fullscreen" className="max-w-full max-h-full rounded-xl object-contain" />
          }
          <button onClick={() => setFullscreen(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center text-xl font-light">
            ×
          </button>
          {(fullscreen.name || fullscreen.prompt) && (
            <div className="absolute bottom-6 left-0 right-0 text-center px-8">
              {fullscreen.name && <p className="text-white font-semibold text-sm mb-1">{fullscreen.name}</p>}
              {fullscreen.prompt && <p className="text-white/60 text-xs">{fullscreen.prompt}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdLibraryPage;
